import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, map } from 'rxjs';
import { IFileIOService, FileDialogFilter } from '@infinitform/platform-core';
import { CLOUD_PLATFORM_CONFIG } from '../config';

@Injectable()
export class FileIOCloudService implements IFileIOService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(CLOUD_PLATFORM_CONFIG);

  readFile(path: string): Observable<string> {
    const url = path.startsWith('http')
      ? path
      : `${this.config.apiBaseUrl}/${path}`;
    return this.http.get(url, { responseType: 'text' });
  }

  readFileBinary(path: string): Observable<ArrayBuffer> {
    const url = path.startsWith('http')
      ? path
      : `${this.config.apiBaseUrl}/${path}`;
    return this.http.get(url, { responseType: 'arraybuffer' });
  }

  writeFile(path: string, data: string | ArrayBuffer): Observable<void> {
    return this.http.put<void>(`${this.config.apiBaseUrl}/${path}`, data);
  }

  copyFile(from: string, to: string): Observable<void> {
    return this.http.post<void>(`${this.config.apiBaseUrl}/files/copy`, {
      from,
      to,
    });
  }

  deleteFile(path: string): Observable<void> {
    return this.http.delete<void>(`${this.config.apiBaseUrl}/files/${encodeURIComponent(path)}`);
  }

  exists(path: string): Observable<boolean> {
    const url = path.startsWith('http')
      ? path
      : `${this.config.apiBaseUrl}/${path}`;
    return this.http.head(url, { observe: 'response' }).pipe(
      map(() => true)
    );
  }

  openFileDialog(filters?: FileDialogFilter[]): Observable<string | null> {
    return from(
      new Promise<string | null>((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';

        if (filters?.length) {
          input.accept = filters
            .flatMap((f) => f.extensions.map((ext) => `.${ext}`))
            .join(',');
        }

        input.onchange = () => {
          const file = input.files?.[0];
          resolve(file ? file.name : null);
        };
        input.oncancel = () => resolve(null);
        input.click();
      })
    );
  }

  saveFileDialog(_defaultPath?: string): Observable<string | null> {
    // Browser environment doesn't support native save dialogs directly.
    // File downloads are triggered via anchor elements in the consuming code.
    return from(Promise.resolve(null));
  }
}
